"use client";

import { Scissors, Ruler, Anchor, Feather, Layers, Hand } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  label: "STRATEGY No. 01",
  title: "Revocable Living Trusts",
  description: "The cornerstone of privacy. We structure trusts that bypass the public probate process, ensuring your family's affairs remain confidential.",
  icon: Layers,
  colStart: "lg:col-start-1",
  offset: "lg:mt-0"
}, {
  label: "STRATEGY No. 02",
  title: "Asset Shielding",
  description: "Utilizing Florida's robust statutory protections to insulate your primary residence and liquid wealth from unforeseen legal liabilities.",
  icon: Scissors,
  colStart: "lg:col-start-5",
  offset: "lg:mt-20"
}, {
  label: "STRATEGY No. 03",
  title: "Legacy Reviews",
  description: "A meticulous review of your current holdings to identify vulnerabilities in your wealth preservation architecture before they are tested.",
  icon: Feather,
  colStart: "lg:col-start-9",
  offset: "lg:-mt-10"
}, {
  label: "STRATEGY No. 04",
  title: "Family Safeguards",
  description: "Comprehensive powers of attorney and healthcare directives that ensure your wishes are executed with precision during times of crisis.",
  icon: Ruler,
  colStart: "lg:col-start-1",
  offset: "lg:mt-12"
}, {
  label: "STRATEGY No. 05",
  title: "Multi-Generational Planning",
  description: "Structuring kid trusts and educational funds that preserve wealth for the next generation while incentivizing responsible stewardship.",
  icon: Anchor,
  colStart: "lg:col-start-5",
  offset: "lg:mt-28"
}, {
  label: "STRATEGY No. 06",
  title: "Business Succession",
  description: "Hard-coded transition plans for Florida business owners, ensuring the enterprise you built continues to provide for your heirs.",
  icon: Hand,
  colStart: "lg:col-start-9",
  offset: "lg:mt-4"
}];
export default function FloridaProtectionStrategies() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const quote = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  return <section data-section-id="4812" id="florida-protection-strategies" className="relative w-full overflow-hidden bg-background py-32 md:py-48">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        {}
        <div ref={header.ref} className={`mx-auto max-w-3xl text-center transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          
          <h2 className="font-default text-5xl md:text-6xl lg:text-[4rem] leading-[1.1] tracking-tight text-foreground">
            The architecture
            <br />
            of <span className="italic text-primary">wealth</span> security.
          </h2>
          <div className="w-2/3 h-[1px] bg-border mx-auto mt-10" />
          <p className="font-default mt-10 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto text-secondary">
            Six foundational pillars of Florida estate law—meticulously applied to 
            ensure your family's legacy remains uncompromised by court intervention 
            or external claims.
          </p>
        </div>

        {}
        <div className="relative mt-28 md:mt-40 grid grid-cols-1 lg:grid-cols-12 gap-y-20 lg:gap-y-32 gap-x-8">
          {features.map((feature, i) => {
          const reveal = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.25
          });
          const Icon = feature.icon;
          return <div data-index={i} ref={reveal.ref} key={i} className={`group lg:col-span-4 ${feature.colStart} ${feature.offset} transition-all duration-1000 ease-out ${reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
            transitionDelay: `${i % 3 * 120}ms`
          }}>
                <div className="pt-8 border-t border-border">
                  <p className="font-mono text-[0.65rem] tracking-[0.2em] uppercase mb-8 text-primary">
                    {feature.label}
                  </p>

                  <div className="mb-10 transition-transform duration-500 ease-out group-hover:-translate-y-1 text-foreground">
                    <Icon className="h-20 w-20 transition-all duration-700 group-hover:text-primary opacity-80 group-hover:opacity-100" strokeWidth={1} style={{
                  strokeDasharray: 200,
                  strokeDashoffset: reveal.isVisible ? 0 : 200,
                  transition: "stroke-dashoffset 1.2s ease-out, color 0.5s ease-out, opacity 0.5s ease-out"
                }} />
                  </div>

                  <h3 className="font-default font-medium text-2xl md:text-[1.75rem] leading-tight mb-5 transition-colors duration-300 text-foreground">
                    {feature.title}
                  </h3>

                  <p className="font-default text-base md:text-lg leading-relaxed text-secondary">
                    {feature.description}
                  </p>
                </div>
              </div>;
        })}
        </div>

        {}
        <div ref={quote.ref} className={`mt-32 md:mt-48 grid grid-cols-12 transition-all duration-1000 ease-out ${quote.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <blockquote className="col-span-12 lg:col-start-3 lg:col-span-8 text-center">
            <p className="font-serif italic text-3xl md:text-4xl lg:text-[2.5rem] leading-[1.25] text-foreground">
              &ldquo;true wealth preservation is not found in a single document, but in the quiet strength of a comprehensive strategy.&rdquo;
            </p>
          </blockquote>
        </div>
      </div>
    </section>;
}